import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useEffect } from "react";
import { isCurrentCoachLink } from "@/lib/leaderSharedCoaching";
import { PREVIEW_COACH_MESSAGES, PREVIEW_COACH_USER_ID } from "@/data/leaderPreviewData";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";

export interface Message {
  id: string;
  coachId: string;
  leaderId: string;
  senderId: string;
  content: string;
  attachmentBucket: string | null;
  attachmentPath: string | null;
  attachmentName: string | null;
  attachmentMimeType: string | null;
  attachmentSizeBytes: number | null;
  createdAt: string;
  read: boolean;
  readAt: string | null;
  isFromCoach: boolean;
}

interface CoachMessageRow {
  id: string;
  coach_id: string;
  leader_id: string;
  sender_id: string;
  content: string;
  attachment_bucket: string | null;
  attachment_path: string | null;
  attachment_name: string | null;
  attachment_mime_type: string | null;
  attachment_size_bytes: number | null;
  created_at: string;
  read: boolean | null;
  read_at: string | null;
}

function isMissingAttachmentColumn(error: unknown): boolean {
  const err = error as { code?: string; message?: string; details?: string; hint?: string } | null;
  if (!err) return false;
  const text = `${err.message || ""} ${err.details || ""} ${err.hint || ""}`.toLowerCase();

  // Postgres unknown-column
  if (err.code === "42703") {
    return (
      text.includes("attachment_bucket") ||
      text.includes("attachment_path") ||
      text.includes("attachment_name") ||
      text.includes("attachment_mime_type") ||
      text.includes("attachment_size_bytes")
    );
  }

  // PostgREST schema cache mismatch (common immediately after schema drift/missing migrations)
  if (text.includes("schema cache") || text.includes("could not find the")) {
    return (
      text.includes("'attachment_bucket'") ||
      text.includes("'attachment_path'") ||
      text.includes("'attachment_name'") ||
      text.includes("'attachment_mime_type'") ||
      text.includes("'attachment_size_bytes'")
    );
  }

  return (
    text.includes("attachment_bucket") ||
    text.includes("attachment_path") ||
    text.includes("attachment_name") ||
    text.includes("attachment_mime_type") ||
    text.includes("attachment_size_bytes")
  );
}

function formatAttachmentColumnMigrationMessage(): string {
  return "Attachment messaging is not fully migrated in this environment. Run migration 20260602113000_add_coach_message_attachments.sql.";
}

interface CoachMessageAccessScope {
  coachId?: string;
  relationshipRevision?: string;
  /** Explicit current-coach scope for the authenticated leader reader. */
  leaderCoachId?: string;
}

export function coachMessageQueryKey({
  leaderId,
  userId,
  coachId,
  relationshipRevision,
}: {
  leaderId: string;
  userId: string | undefined;
  coachId?: string;
  relationshipRevision?: string;
  /** Explicit current-coach scope for the authenticated leader reader. */
  leaderCoachId?: string;
}) {
  return [
    "coach-messages",
    leaderId,
    userId ?? "anonymous",
    coachId ?? "leader-view",
    relationshipRevision ?? "current-relationship",
  ] as const;
}

export function useCoachMessages(
  leaderId: string,
  accessScope: CoachMessageAccessScope = {},
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { coachId, relationshipRevision, leaderCoachId } = accessScope;

  const query = useQuery({
    queryKey: coachMessageQueryKey({
      leaderId,
      userId: user?.id,
      coachId: coachId ?? leaderCoachId,
      relationshipRevision,
    }),
    queryFn: async (): Promise<Message[]> => {
      if (!user || !leaderId) return [];
      if (coachId && coachId !== user.id) return [];
      if (leaderCoachId && user.id !== leaderId) return [];
      if (user.id === PREVIEW_LEADER_USER_ID) {
        return PREVIEW_COACH_MESSAGES.messages.map((message) => ({
          id: message.id,
          coachId: PREVIEW_COACH_USER_ID,
          leaderId,
          senderId: message.senderId === "coach" ? PREVIEW_COACH_USER_ID : PREVIEW_LEADER_USER_ID,
          content: message.content,
          attachmentBucket: message.id === "preview-shared-document" ? "coach_message_attachments" : null,
          attachmentPath: message.id === "preview-shared-document" ? `leader/${leaderId}/coach-messages/session-notes.pdf` : null,
          attachmentName: message.id === "preview-shared-document" ? "Session reflection questions.pdf" : null,
          attachmentMimeType: null,
          attachmentSizeBytes: null,
          createdAt: message.createdAt,
          read: true,
          readAt: message.createdAt,
          isFromCoach: message.senderId === "coach",
        }));
      }

      if (leaderCoachId && !await isCurrentCoachLink(supabase, {leaderId,coachId:leaderCoachId})) return [];

      let messageQuery = supabase
        .from("coach_messages")
        .select("*")
        .eq("leader_id", leaderId);

      if (coachId || leaderCoachId) {
        messageQuery = messageQuery.eq("coach_id", coachId ?? leaderCoachId!);
      }

      const { data, error } = await messageQuery.order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching messages:", error);
        // Throw error instead of returning [] to prevent flickering
        throw error;
      }

      if (leaderCoachId && !await isCurrentCoachLink(supabase, {leaderId,coachId:leaderCoachId})) return [];
      return ((data || []) as CoachMessageRow[]).map((msg) => ({
        id: msg.id,
        coachId: msg.coach_id,
        leaderId: msg.leader_id,
        senderId: msg.sender_id,
        content: msg.content,
        attachmentBucket: msg.attachment_bucket,
        attachmentPath: msg.attachment_path,
        attachmentName: msg.attachment_name,
        attachmentMimeType: msg.attachment_mime_type,
        attachmentSizeBytes: msg.attachment_size_bytes,
        createdAt: msg.created_at,
        read: msg.read || false,
        readAt: msg.read_at,
        isFromCoach: msg.sender_id === msg.coach_id,
      }));
    },
    enabled: !!user && !!leaderId,
    // Reduce aggressive refetching
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: Boolean(leaderCoachId),
    refetchInterval: leaderCoachId ? 60_000 : false,
    refetchIntervalInBackground: false,
    retry: 2,
  });

  // Real-time subscription for new messages and read status updates
  useEffect(() => {
    if (user?.id === PREVIEW_LEADER_USER_ID) return;
    if (!user || !leaderId) return;

    const channel = supabase
      .channel(`messages-${leaderId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "coach_messages",
          filter: `leader_id=eq.${leaderId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["coach-messages", leaderId] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "coach_messages",
          filter: `leader_id=eq.${leaderId}`,
        },
        () => {
          // Refresh when messages are marked as read
          queryClient.invalidateQueries({ queryKey: ["coach-messages", leaderId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, leaderId]);

  return query;
}

export function useSendMessage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    retry: 0,
    mutationFn: async ({
      leaderId,
      coachId,
      content,
      attachment,
    }: {
      leaderId: string;
      coachId: string;
      content: string;
      attachment?: {
        bucket: string;
        path: string;
        name: string;
        mimeType: string;
        sizeBytes: number;
      } | null;
    }) => {
      if (!user) throw new Error("Not authenticated");
      if (user.id === PREVIEW_LEADER_USER_ID) return;
      const trimmedContent = content.trim();
      const resolvedContent = trimmedContent || (attachment ? `Shared a document: ${attachment.name}` : "");
      const basePayload = {
        leader_id: leaderId,
        coach_id: coachId,
        sender_id: user.id,
        content: resolvedContent,
      };

      if (!attachment) {
        const { error } = await supabase.from("coach_messages").insert(basePayload);
        if (error) throw error;
        return;
      }

      const insertPayload = {
        ...basePayload,
        attachment_bucket: attachment?.bucket || null,
        attachment_path: attachment?.path || null,
        attachment_name: attachment?.name || null,
        attachment_mime_type: attachment?.mimeType || null,
        attachment_size_bytes: attachment?.sizeBytes || null,
      };

      const { error } = await supabase.from("coach_messages").insert(insertPayload);

      // Keep text-only compatibility for legacy schemas, but block attachment sends
      // so files are never silently dropped.
      if (error && isMissingAttachmentColumn(error)) {
        // Remove uploaded object to avoid orphaned files in partial-migration environments.
        if (attachment?.bucket && attachment?.path) {
          await supabase.storage.from(attachment.bucket).remove([attachment.path]);
        }
        throw new Error(formatAttachmentColumnMigrationMessage());
      }

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["coach-messages", variables.leaderId],
      });
    },
  });
}

export function useMarkMessagesRead() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leaderId: string) => {
      if (!user) throw new Error("Not authenticated");
      if (user.id === PREVIEW_LEADER_USER_ID) return 0;

      const { count, error: countError } = await supabase
        .from("coach_messages")
        .select("*", { count: "exact", head: true })
        .eq("leader_id", leaderId)
        .neq("sender_id", user.id)
        .eq("read", false);

      if (countError) throw countError;
      if (!count) return 0;

      const { error } = await supabase
        .from("coach_messages")
        .update({ read: true, read_at: new Date().toISOString() })
        .eq("leader_id", leaderId)
        .neq("sender_id", user.id)
        .eq("read", false);

      if (error) throw error;

      return count;
    },
    onSuccess: (updatedCount, leaderId) => {
      if (!updatedCount) return;
      queryClient.invalidateQueries({ queryKey: ["coach-messages", leaderId] });
      queryClient.invalidateQueries({ queryKey: ["coach-leaders-command"] });
      queryClient.invalidateQueries({ queryKey: ["unread-messages"] });
    },
  });
}

export function useUnreadMessageCount(leaderId?: string, coachId?: string, { requireCoachScope=false }: { requireCoachScope?:boolean } = {}) {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["unread-messages", user?.id ?? null, leaderId ?? null, coachId ?? null, requireCoachScope],
    queryFn: async () => {
      if (!user) return 0;
      if (user.id === PREVIEW_LEADER_USER_ID) return 0;
      const scopedLeader = Boolean(leaderId && coachId && user.id === leaderId);
      if (scopedLeader && !await isCurrentCoachLink(supabase, { leaderId: leaderId!, coachId: coachId! })) throw new Error('Your coach connection changed.');

      let query = supabase
        .from("coach_messages")
        .select("*", { count: "exact", head: true })
        .neq("sender_id", user.id)
        .or("read.is.null,read.eq.false");

      if (leaderId) {
        query = query.eq("leader_id", leaderId);
      }
      if (coachId) query = query.eq('coach_id', coachId);

      const { count, error } = await query;

      if (error) {
        throw new Error('Message counts are unavailable.');
      }
      if (scopedLeader && !await isCurrentCoachLink(supabase, { leaderId: leaderId!, coachId: coachId! })) throw new Error('Your coach connection changed.');

      return count || 0;
    },
    enabled: !!user && user.id !== PREVIEW_LEADER_USER_ID && (!coachId || Boolean(leaderId)) && (!requireCoachScope || Boolean(leaderId && coachId && user.id===leaderId)),
    retry: false,
    refetchInterval: 60_000,
  });
  return {...query,data:!query.isFetching&&!query.isError?query.data:undefined};
}
